import {useCallback, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';

interface DocumentListUrlState {
  search: string;
  setSearch: (value: string) => void;
  currentPage: number;
  setCurrentPage: (value: number) => void;
}

const parsePage = (value: string | null): number => {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : 1;
};

export const useDocumentListUrlState = (): DocumentListUrlState => {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') ?? '';
  const currentPage = parsePage(searchParams.get('page'));

  const setParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams(
        previous => {
          const next = new URLSearchParams(previous);
          Object.entries(updates).forEach(([key, value]) => {
            if (value === undefined || value === '') {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          });
          return next;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  const setSearch = useCallback(
    (value: string) => setParams({search: value, page: undefined}),
    [setParams]
  );

  const setCurrentPage = useCallback(
    (value: number) =>
      setParams({page: value === 1 ? undefined : String(value)}),
    [setParams]
  );

  return useMemo(
    () => ({search, setSearch, currentPage, setCurrentPage}),
    [search, setSearch, currentPage, setCurrentPage]
  );
};
